import AppOverlay from '../Layout/AppOverlay';
import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getVariantStorageKey,
} from '../../data/prayerVariants';
import { applyVariantToVoiceLang, getVoicePrefs } from '../../utils/voicePrefs';
import PrayerRecorder from '../common/PrayerRecorder';

import OptionalPrayerSheet from '../common/OptionalPrayerSheet';
import OfferingLight from '../common/OfferingLight';
import { loadPrayForIntentions } from '../../utils/prayForStore';
import {
  getBookletStepContext,
  stepContextToVitralVars,
  makeBookletRoseFingerprint,
  getOngoingMysteryDecade,
} from '../../utils/bookletProgress';
import { playBookletTransitionSound, playOfferingChime } from '../../utils/bookletSounds';
import {
  pickPrayerImage,
  occurrenceIndexAt,
  dailyRotationOffset,
  getLitanyVerseImageCandidates,
  resolveLitanyVerseImage,
  getPrayerImageCandidates,
  countPrayerPinnedSources,
} from '../../utils/prayerImages';
import { getLitanyVerse, isLitanyPrayer, formatLitanyLine } from '../../utils/litanyHelpers';
import {
  getPrayerVerseCount,
  getPrayerVerseText,
  getPrayerVerseImageCandidates,
  resolvePrayerVerseImage,
} from '../../utils/prayerVerseImages';
import { supportsPerVerseImages } from '../../data/prayerVerseCatalog';
import LitanyEntrance from '../Litany/LitanyEntrance';
import VitralBackground from '../common/VitralBackground';
import BookletPrayerPanel from './BookletPrayerPanel';
import { buildSequence, isDivineMercyMode, isSagradoCorazonAdoracionMode, isValidRosaryMystery, getNextRosaryMystery, hidesRosaryPills, getNovenaConfig, getNovenaDays } from '../../utils/bookletSequence';
import { getDefaultMystery } from '../utils/getDefaultMystery';
import { imagePath as registryImage, isTextHeavyImagePath } from '../../data/imageRegistry';
import {
  ANGELUS_ID,
  MAGNIFICAT_ID,
  angelusThumbnail,
  magnificatThumbnail,
} from '../../data/marianDevotionsData';
import {
  SAGRADO_CORAZON_ADORACION_ID,
  sagradoCorazonAdoracionThumbnail,
} from '../../data/sagradoCorazonAdoracionData';
import { MEMORARE_ID, memorareThumbnail } from '../../data/memorareData';
import { MILAGROSA_ID, milagrosaThumbnail } from '../../data/medallaMilagrosaData';
import {
  INMACULADO_CORAZON_ID,
  inmaculadoCorazonThumbnail,
} from '../../data/inmaculadoCorazonData';
import {
  ESPIRITU_SANTO_ID,
  holySpiritNovenaThumbnail,
} from '../../data/espirituSantoNovenaData';
import {
  DIVINE_MERCY_ID,
  DIVINE_MERCY_NOVENA_ID,
  chapletThumbnail,
  novenaThumbnail,
} from '../../data/divineMercyData';
import StationsDevotionThumb from '../common/StationsDevotionThumb';
import MercyWindowThumb from '../common/MercyWindowThumb';
import DevotionsShelf, { ShelfItem } from '../common/DevotionsShelf';
import { optionalPrayerThumbnail } from '../../data/optionalPrayers';
import { upcomingDevotionThumbs } from '../../data/historicDevotionsCatalog';
import { loadSavedVariantId, resolveDisplayText, resolveEnLiberDisplayText, resolveLiberTitle } from '../../utils/bookletDisplayText';
import { getSpeakablePrayerText } from '../../utils/speakablePrayerText';
import { isPlaylistActive, peekPendingAutoMystery, clearPendingAuto } from '../../utils/devotionPlaylistRunner';
import { getAveMariaRunInfo } from '../../utils/aveMariaRunInfo';
import {
  playStepVoice,
  stopStepVoice,
  nextVoiceMode,
  VOICE_MODES,
} from '../../utils/prayerVoicePlayback';
import PrayerShareCard from '../common/PrayerShareCard';
import PrayerSharePreviewModal from '../common/PrayerSharePreviewModal';
import BookletOutlineView from './BookletOutlineView';
import {
  buildShareCardPayload,
  buildShareDeepLink,
  buildSharePrayerText,
  captureShareCardPng,
  deliverSharePng,
  formatBookletShareProgress,
  getBookletDevotionLabel,
  makeShareFilename,
  waitForShareReady,
} from '../../utils/bookletShare';
import './BookletView.css';
import { devLog } from '../../utils/devotionsDebug';

const TRANSITION_PHASE = {
  READY: 'ready',
  LEAVING: 'leaving',
  LINGER: 'linger',
  ARRIVING: 'arriving',
  ENTERING: 'entering',
};

export const BOOKLET_TIMING = {
  /** Minimum gap before vitral swaps after prayer chrome leaves */
  minGap: 72,
  textOut: 220,
  linger: 280,
  imageBeforeText: 480,
  textIn: 300,
  stepGlow: 380,
};

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const MYSTERY_OPTIONS = [
  {
    id: 'gozosos',
    label: 'Gozosos',
    img: '/gallery-images/misterios/modooscuro/misteriogozo0.webp',
  },
  {
    id: 'dolorosos',
    label: 'Dolorosos',
    img: '/gallery-images/misterios/modooscuro/misteriodolor0.jpg',
  },
  {
    id: 'gloriosos',
    label: 'Gloriosos',
    img: '/gallery-images/misterios/modooscuro/misteriogloria0.jpg',
  },
  {
    id: 'luminosos',
    label: 'Luminosos',
    img: '/gallery-images/misterios/modooscuro/misterioLUZ0.webp',
  },
];

/** Recorridos del estante; 'stations' tiene thumb propio (menú Vía Crucis / Lucis). */
const SHELF_RECORRIDOS = [
  { type: 'stations', label: 'Estaciones' },
  {
    id: 'sangrepreciosa_litany',
    label: 'Letanía Sangre',
    title: 'Letanía de la Preciosísima Sangre (Julio)',
    img: registryImage('vitreauxCruz'),
    badge: 'L',
  },
  {
    id: 'sangrepreciosa_chaplet',
    label: 'Corona Sangre',
    title: 'Corona de la Preciosísima Sangre',
    img: registryImage('vitralAgnusDei'),
    badge: 'C',
  },
  {
    id: 'sangrepreciosa_ofrendas',
    label: '7 Ofrendas',
    title: 'Siete Ofrendas de la Sangre de Cristo',
    img: registryImage('vitralCaliz'),
    badge: '7',
  },
  {
    id: SAGRADO_CORAZON_ADORACION_ID,
    label: 'Sagrado Corazón',
    title: 'Adoración Eucarística — Sagrado Corazón de Jesús',
    img: sagradoCorazonAdoracionThumbnail,
    badge: 'SC',
  },
  {
    id: DIVINE_MERCY_ID,
    label: 'Corona Miseric.',
    title: 'Corona de la Divina Misericordia — Santa Faustina',
    img: chapletThumbnail,
    badge: 'DM',
  },
  {
    id: DIVINE_MERCY_NOVENA_ID,
    label: 'Novena Miseric.',
    title: 'Novena de la Divina Misericordia — Santa Faustina',
    img: novenaThumbnail,
    isNovena: true,
  },
  {
    id: ANGELUS_ID,
    label: 'Ángelus',
    title: 'Ángelus',
    img: angelusThumbnail,
    badge: 'A',
  },
  {
    id: MAGNIFICAT_ID,
    label: 'Magnificat',
    title: 'Magnificat',
    img: magnificatThumbnail,
    badge: 'M',
  },
  {
    id: MEMORARE_ID,
    label: 'Memorare',
    title: 'Memorare — Virgen Madre',
    img: memorareThumbnail,
    badge: 'R',
  },
  {
    id: MILAGROSA_ID,
    label: 'Medalla',
    title: 'Medalla Milagrosa — Inmaculada',
    img: milagrosaThumbnail,
    badge: 'MM',
  },
  {
    id: INMACULADO_CORAZON_ID,
    label: 'Inmaculado C.',
    title: 'Inmaculado Corazón de María — Fátima',
    img: inmaculadoCorazonThumbnail,
    badge: 'IC',
  },
  {
    id: ESPIRITU_SANTO_ID,
    label: 'Espíritu Santo',
    title: 'Novena al Espíritu Santo — Pentecostés',
    img: holySpiritNovenaThumbnail,
    isNovena: true,
  },
];

/** Oraciones breves del estante: abren la hoja opcional por id. */
const SHELF_BREVES = [
  { prayerId: 'carmen', label: 'Virgen del Carmen', title: 'Virgen del Carmen — 16 de julio', badge: '16' },
  { prayerId: 'escapulario', label: 'Escápulario', title: 'Escápulario del Carmen — la Promesa', badge: 'E' },
  { prayerId: 'guardian', label: 'Ángel Guarda', title: 'Ángel de la Guarda', badge: 'Á' },
  { prayerId: 'michael', label: 'San Miguel', title: 'San Miguel Arcángel', badge: 'SM' },
  { prayerId: 'expedito', label: 'San Expedito', title: 'San Expedito — causas urgentes (HODIE)', badge: 'H' },
  { prayerId: 'benedict', label: 'San Benito', title: 'San Benito', badge: 'B' },
  { prayerId: 'patrick', label: 'San Patricio', title: 'San Patricio — Coraza (Breastplate)', badge: 'P' },
  { prayerId: 'cayetano', label: 'San Cayetano', title: 'San Cayetano — pan y trabajo', badge: 'Ct' },
  { prayerId: 'salve', label: 'Salve Regina', title: 'Salve Regina — cierre mariano', badge: 'S' },
  { prayerId: 'bendito', label: 'Bendito sea Dios', title: 'Bendito sea Dios — alabanzas', badge: '✝' },
];

function renderShelfRecorrido(item, { misterioActual, onShelfDevotionClick, onMysteryChange }) {
  if (item.type === 'stations') {
    return (
      <ShelfItem key={item.label} label={item.label}>
        <StationsDevotionThumb
          misterioActual={misterioActual}
          onMysteryChange={onMysteryChange}
        />
      </ShelfItem>
    );
  }
  return (
    <ShelfItem key={item.id} label={item.label}>
      <MercyWindowThumb
        active={misterioActual === item.id}
        onClick={(e) => onShelfDevotionClick(e, item.id, item.label)}
        title={item.title}
        img={item.img}
        badge={item.badge}
        isNovena={item.isNovena}
      />
    </ShelfItem>
  );
}

function getDevotionChrome(misterioActual) {
  return getBookletDevotionLabel(misterioActual);
}

function getBookletDisplayTitle(prayer, voiceLang, mystery) {
  return resolveLiberTitle(prayer, voiceLang, mystery);
}


export default function BookletView({
  currentPrayerIndex,
  misterioActual,
  onUpdateProgreso,
  onMysteryChange,
  isLeftHanded = false,
  simpleMode = false,
  soundEnabled = true,
  mercyOptionalOpening = true,
  litanyEntranceEnabled = true,
  perVersePrayerImages = false,
  onAveMariaComplete,
  onAveMariaUndo,
  novenaDay = 1,
  onNovenaDayChange,
  openOptionalId = null,
}) {
  const isMercy = isDivineMercyMode(misterioActual);
  const isSagradoCorazon = isSagradoCorazonAdoracionMode(misterioActual);
  // Devotion chrome (pill row, header actions) is centralised in
  // hidesRosaryPills so a new devotion cannot forget to opt out.
  const showRosaryPills = !hidesRosaryPills(misterioActual);
  const pickDevotion = useCallback(
    (id, label) => {
      devLog('shelf-pick', { id, label, from: misterioActual });
      onMysteryChange?.(id);
    },
    [misterioActual, onMysteryChange]
  );

  const onShelfDevotionClick = useCallback(
    (e, id, label) => {
      e?.stopPropagation?.();
      pickDevotion(id, label);
    },
    [pickDevotion]
  );
  const secuencia = useMemo(
    () => buildSequence(misterioActual, { includeMercyOpening: mercyOptionalOpening, novenaDay }),
    [misterioActual, mercyOptionalOpening, novenaDay]
  );

  useEffect(() => {
    if (showRosaryPills) return;
    devLog('sequence-ready', {
      misterio: misterioActual,
      steps: secuencia.length,
      first: secuencia[0]?.title,
    });
  }, [misterioActual, secuencia, showRosaryPills]);

  const total = secuencia.length;
  const safeIndex = Math.min(
    Math.max(currentPrayerIndex, 0),
    Math.max(total - 1, 0)
  );
  const [displayIndex, setDisplayIndex] = useState(safeIndex);
  // How long the current step has been on screen — feeds the rose fingerprint
  // when an Ave María is completed by advancing past it.
  const stepShownAtRef = useRef(Date.now());
  useEffect(() => {
    stepShownAtRef.current = Date.now();
  }, [displayIndex]);
  const [transitionPhase, setTransitionPhase] = useState(TRANSITION_PHASE.READY);
  const transitionPhaseRef = useRef(TRANSITION_PHASE.READY);
  const transitionTimersRef = useRef([]);
  const imageReadyRef = useRef(true);
  const afterImageReadyRef = useRef(null);
  const activePrayer = secuencia[displayIndex];
  /**
   * Which repetition of `activePrayer` this is. A rosary has 50 Ave Marías and
   * they all share one prayer object, so without this every one of them drew
   * from the same tiny pool at the same phase.
   */
  const occurrence = useMemo(
    () => occurrenceIndexAt(secuencia, displayIndex),
    [secuencia, displayIndex]
  );
  // Today's start point in the pool, so tomorrow's rosary is not a replay.
  const dayOffset = useMemo(
    () => dailyRotationOffset(secuencia, displayIndex),
    [secuencia, displayIndex]
  );
  const isTransitioning = transitionPhase !== TRANSITION_PHASE.READY;

  const variants = activePrayer?.variants;
  const [voiceLangPref, setVoiceLangPref] = useState(
    () => getVoicePrefs().voiceLang || 'es'
  );
  const [variantId, setVariantId] = useState(() =>
    loadSavedVariantId(activePrayer, getVoicePrefs().voiceLang || 'es')
  );
  const [stepGlow, setStepGlow] = useState(false);
  const [offeringLight, setOfferingLight] = useState(false);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [optionalPrayerId, setOptionalPrayerId] = useState('guardian');
  const [optionalGlow, setOptionalGlow] = useState(false);
  const optionalIdleRef = useRef(null);
  const mountedRef = useRef(true);
  const [shelfOpen, setShelfOpen] = useState(false);
  const prevIndexRef = useRef(safeIndex);
  const isFirstRenderRef = useRef(true);
  const [litanyVerseIndex, setLitanyVerseIndex] = useState(0);
  const [prayerVerseIndex, setPrayerVerseIndex] = useState(0);
  const [showLitanyEntrance, setShowLitanyEntrance] = useState(false);
  const litanyEntranceShownRef = useRef(false);
  const shareCardRef = useRef(null);
  const shareBlobRef = useRef(null);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);
  const [sharePreviewUrl, setSharePreviewUrl] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [voiceMode, setVoiceMode] = useState(VOICE_MODES.OFF);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const voiceModeRef = useRef(VOICE_MODES.OFF);
  const canAdvanceVoiceRef = useRef(false);
  /** When auto chains to next via, keep ≫ instead of resetting on misterio change. */
  const preserveAutoOnMysteryChangeRef = useRef(false);
  /** Mystery where auto session started — stop after returning (four vias). */
  const autoRosaryStartRef = useRef(null);
  const misterioActualRef = useRef(misterioActual);
  misterioActualRef.current = misterioActual;
  const playCurrentVoiceStepRef = useRef(null);

  // Always-on step autoplay removed — Liber ▶ owns once/auto (see prayerVoicePlayback).

  const isLitany = isLitanyPrayer(activePrayer);
  const isPerVersePrayer = perVersePrayerImages && supportsPerVerseImages(activePrayer?.id);
  const prayerVerseTotal = isPerVersePrayer ? getPrayerVerseCount(activePrayer.id) : 0;
  const litanyVerse = isLitany ? getLitanyVerse(litanyVerseIndex, activePrayer) : null;
  const litanyVerseTotal = activePrayer?.verses?.length || 0;
  const innerVerseIndex = isLitany ? litanyVerseIndex : prayerVerseIndex;
  const innerVerseTotal = isLitany ? litanyVerseTotal : prayerVerseTotal;
  const hasInnerVerses = isLitany || isPerVersePrayer;

  useEffect(() => {
    if (activePrayer?.id === 'LL') {
      setLitanyVerseIndex(0);
      if (litanyEntranceEnabled && !litanyEntranceShownRef.current) {
        setShowLitanyEntrance(true);
        litanyEntranceShownRef.current = true;
      }
    } else {
      setLitanyVerseIndex(0);
      setShowLitanyEntrance(false);
    }
    setPrayerVerseIndex(0);
  }, [displayIndex, activePrayer?.id, litanyEntranceEnabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetOptionalIdle = useCallback(() => {
    setOptionalGlow(false);
    if (optionalIdleRef.current) clearTimeout(optionalIdleRef.current);
    optionalIdleRef.current = setTimeout(() => {
      if (mountedRef.current) setOptionalGlow(true);
    }, 30000);
  }, []);

  const openOptionalPrayer = useCallback((id = 'guardian') => {
    setOptionalPrayerId(id);
    setShelfOpen(false);
    setOptionalOpen(true);
    resetOptionalIdle();
  }, [resetOptionalIdle]);

  // Deep link ?oracion=expedito (etc.) — open once when Libro mounts with id.
  useEffect(() => {
    if (!openOptionalId || !optionalPrayerThumbnail(openOptionalId)) return;
    openOptionalPrayer(openOptionalId);
  }, [openOptionalId, openOptionalPrayer]);

  useEffect(() => {
    resetOptionalIdle();
    return () => {
      if (optionalIdleRef.current) clearTimeout(optionalIdleRef.current);
    };
  }, [safeIndex, misterioActual, optionalOpen, resetOptionalIdle]);

  const setPhase = useCallback((phase) => {
    transitionPhaseRef.current = phase;
    setTransitionPhase(phase);
  }, []);

  const clearTransitionTimers = useCallback(() => {
    transitionTimersRef.current.forEach((id) => clearTimeout(id));
    transitionTimersRef.current = [];
  }, []);

  const scheduleTransition = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    transitionTimersRef.current.push(id);
    return id;
  }, []);

  useEffect(() => () => clearTransitionTimers(), [clearTransitionTimers]);

  useEffect(() => {
    clearTransitionTimers();
    setPhase(TRANSITION_PHASE.READY);
    setDisplayIndex(safeIndex);
  }, [misterioActual, clearTransitionTimers, setPhase, safeIndex]);

  useEffect(() => {
    if (transitionPhaseRef.current === TRANSITION_PHASE.READY && displayIndex !== safeIndex) {
      setDisplayIndex(safeIndex);
    }
  }, [safeIndex, displayIndex]);

  const beginTextEnter = useCallback(() => {
    setPhase(TRANSITION_PHASE.ENTERING);
    scheduleTransition(() => setPhase(TRANSITION_PHASE.READY), BOOKLET_TIMING.textIn);
  }, [scheduleTransition, setPhase]);

  const beginArrivingHold = useCallback(() => {
    imageReadyRef.current = false;
    afterImageReadyRef.current = () => {
      scheduleTransition(beginTextEnter, BOOKLET_TIMING.imageBeforeText);
    };
    scheduleTransition(() => {
      if (!afterImageReadyRef.current) return;
      imageReadyRef.current = true;
      const run = afterImageReadyRef.current;
      afterImageReadyRef.current = null;
      run();
    }, 900);
  }, [beginTextEnter, scheduleTransition]);

  const handleImageReady = useCallback(() => {
    imageReadyRef.current = true;
    if (
      transitionPhaseRef.current === TRANSITION_PHASE.ARRIVING &&
      afterImageReadyRef.current
    ) {
      const run = afterImageReadyRef.current;
      afterImageReadyRef.current = null;
      run();
    }
  }, []);

  useEffect(() => {
    const onPrefs = (e) => {
      if (e?.detail?.voiceLang) setVoiceLangPref(e.detail.voiceLang);
    };
    window.addEventListener('rosario-voice-prefs', onPrefs);
    return () => window.removeEventListener('rosario-voice-prefs', onPrefs);
  }, []);

  useEffect(() => {
    if (!activePrayer?.variants?.length) {
      setVariantId(null);
      return;
    }
    const next = loadSavedVariantId(activePrayer, voiceLangPref);
    setVariantId(next);
    try {
      if (next) localStorage.setItem(getVariantStorageKey(activePrayer.id), next);
    } catch (_) { /* ignore */ }
  }, [activePrayer, voiceLangPref]);

  const displayText = useMemo(() => {
    const en = resolveEnLiberDisplayText(activePrayer, misterioActual);
    if (String(voiceLangPref || 'es').toLowerCase().startsWith('en') && en) return en;
    if (isPerVersePrayer) {
      return getPrayerVerseText(activePrayer.id, prayerVerseIndex) || '';
    }
    return resolveDisplayText(activePrayer, variantId);
  }, [
    activePrayer,
    variantId,
    isPerVersePrayer,
    prayerVerseIndex,
    voiceLangPref,
    misterioActual,
  ]);

  const bumpInnerVerse = useCallback((delta) => {
    if (isLitany) {
      setLitanyVerseIndex((prev) => {
        const max = litanyVerseTotal - 1;
        if (max < 0) return 0;
        return Math.min(Math.max(prev + delta, 0), max);
      });
    } else if (isPerVersePrayer) {
      setPrayerVerseIndex((prev) => {
        const max = prayerVerseTotal - 1;
        if (max < 0) return 0;
        return Math.min(Math.max(prev + delta, 0), max);
      });
    }
    setStepGlow(true);
    scheduleTransition(() => setStepGlow(false), BOOKLET_TIMING.stepGlow);
  }, [isLitany, isPerVersePrayer, litanyVerseTotal, prayerVerseTotal, scheduleTransition]);

  const cycleVariant = useCallback(() => {
    if (!variants?.length) return;
    const idx = variants.findIndex((v) => v.id === variantId);
    const next = variants[(idx + 1) % variants.length];
    setVariantId(next.id);
    applyVariantToVoiceLang(next.id);
    try {
      localStorage.setItem(getVariantStorageKey(activePrayer.id), next.id);
    } catch (_) { /* ignore */ }
  }, [variants, variantId, activePrayer?.id]);

  const navigateTo = useCallback(
    (newIndex) => {
      if (newIndex < 0 || newIndex >= total || newIndex === displayIndex) return;
      if (transitionPhaseRef.current !== TRANSITION_PHASE.READY) return;

      const oldIndex = displayIndex;
      const leaving = secuencia[oldIndex];
      const entering = secuencia[newIndex];
      const enteringCtx = getBookletStepContext(secuencia, newIndex, total);

      if (newIndex > oldIndex) {
        if (leaving?.id === 'A' && !isMercy) {
          const fp = makeBookletRoseFingerprint(
            getAveMariaRunInfo(secuencia, oldIndex),
            getOngoingMysteryDecade(secuencia, oldIndex),
            {
              source: 'libro',
              timestamp: Date.now(),
              dwellMs: Math.max(0, Date.now() - stepShownAtRef.current),
            }
          );
          onAveMariaComplete?.(fp);
        }
      } else if (newIndex < oldIndex && secuencia[newIndex]?.id === 'A' && !isMercy) {
        onAveMariaUndo?.();
      }

      const finishSwap = () => {
        setDisplayIndex(newIndex);
        onUpdateProgreso(newIndex);
        playBookletTransitionSound({
          prayerId: entering?.id,
          stepContext: enteringCtx,
          soundEnabled,
        });
        setStepGlow(true);
        scheduleTransition(() => setStepGlow(false), BOOKLET_TIMING.stepGlow);
        setPhase(TRANSITION_PHASE.ARRIVING);
        beginArrivingHold();
        scheduleTransition(() => {
          if (!afterImageReadyRef.current) return;
          imageReadyRef.current = true;
          handleImageReady();
        }, 48);
      };

      if (newIndex > oldIndex) {
        setOfferingLight(true);
        playOfferingChime(soundEnabled);
        scheduleTransition(() => setOfferingLight(false), 900);
      }

      if (prefersReducedMotion()) {
        setDisplayIndex(newIndex);
        onUpdateProgreso(newIndex);
        playBookletTransitionSound({
          prayerId: entering?.id,
          stepContext: enteringCtx,
          soundEnabled,
        });
        setPhase(TRANSITION_PHASE.READY);
        return;
      }

      setPhase(TRANSITION_PHASE.LEAVING);
      scheduleTransition(() => {
        setPhase(TRANSITION_PHASE.LINGER);
        scheduleTransition(finishSwap, BOOKLET_TIMING.linger);
      }, BOOKLET_TIMING.textOut);
    },
    [
      displayIndex,
      secuencia,
      total,
      onUpdateProgreso,
      onAveMariaComplete,
      onAveMariaUndo,
      soundEnabled,
      isMercy,
      scheduleTransition,
      setPhase,
      beginArrivingHold,
      handleImageReady,
    ]
  );

  const goPrev = useCallback(() => {
    if (hasInnerVerses && innerVerseIndex > 0) {
      bumpInnerVerse(-1);
      return;
    }
    if (displayIndex > 0) navigateTo(displayIndex - 1);
  }, [hasInnerVerses, innerVerseIndex, bumpInnerVerse, navigateTo, displayIndex]);

  const goNext = useCallback(() => {
    if (hasInnerVerses && innerVerseIndex < innerVerseTotal - 1) {
      bumpInnerVerse(1);
      return;
    }
    if (displayIndex < total - 1) navigateTo(displayIndex + 1);
  }, [hasInnerVerses, innerVerseIndex, innerVerseTotal, bumpInnerVerse, navigateTo, displayIndex, total]);

  voiceModeRef.current = voiceMode;
  canAdvanceVoiceRef.current =
    (hasInnerVerses && innerVerseIndex < innerVerseTotal - 1) || displayIndex < total - 1;

  const speakableText = useMemo(() => {
    if (String(voiceLangPref || 'es').toLowerCase().startsWith('en')) {
      const en = resolveEnLiberDisplayText(activePrayer, misterioActual);
      if (en) return getSpeakablePrayerText({ ...activePrayer, text: en });
    }
    if (isLitany && litanyVerse) return formatLitanyLine(litanyVerse);
    if (isPerVersePrayer) {
      return getPrayerVerseText(activePrayer?.id, prayerVerseIndex) || '';
    }
    return getSpeakablePrayerText({
      ...activePrayer,
      text: displayText || activePrayer?.text,
    });
  }, [
    isLitany,
    litanyVerse,
    isPerVersePrayer,
    activePrayer,
    prayerVerseIndex,
    displayText,
    voiceLangPref,
    misterioActual,
  ]);

  const voiceStepKey = `${misterioActual}:${displayIndex}:${innerVerseIndex}:${activePrayer?.id || ''}:${voiceLangPref}`;

  useEffect(() => {
    if (preserveAutoOnMysteryChangeRef.current) {
      preserveAutoOnMysteryChangeRef.current = false;
      stopStepVoice();
      setVoicePlaying(false);
      return;
    }
    voiceModeRef.current = VOICE_MODES.OFF;
    setVoiceMode(VOICE_MODES.OFF);
    setVoicePlaying(false);
    stopStepVoice();
    autoRosaryStartRef.current = null;
  }, [misterioActual]);

  const finishVoiceResult = useCallback(
    (result) => {
      setVoicePlaying(false);
      if (result?.cancelled) return;
      const mode = voiceModeRef.current;
      if (mode === VOICE_MODES.ONCE) {
        setVoiceMode(VOICE_MODES.OFF);
        return;
      }
      if (mode === VOICE_MODES.AUTO) {
        if (canAdvanceVoiceRef.current) {
          goNext();
          return;
        }
        const nextVia = getNextRosaryMystery(misterioActual);
        const startVia = autoRosaryStartRef.current;
        // Playlist mode: end this devotion and hand off (skip rosary via-chain).
        const playlistActive = isPlaylistActive();
        if (
          !playlistActive &&
          nextVia &&
          startVia &&
          nextVia !== startVia &&
          isValidRosaryMystery(misterioActual)
        ) {
          preserveAutoOnMysteryChangeRef.current = true;
          onMysteryChange?.(nextVia);
          return;
        }
        autoRosaryStartRef.current = null;
        setVoiceMode(VOICE_MODES.OFF);
        if (playlistActive) {
          window.dispatchEvent(
            new CustomEvent('rosario-playlist-devotion-done', {
              detail: { mysteryId: misterioActual },
            })
          );
        }
      }
    },
    [goNext, misterioActual, onMysteryChange]
  );

  const playCurrentVoiceStep = useCallback(async () => {
    // Guide clips are independent of “Efectos de Sonido” (organ/chimes).
    if (isTransitioning) return;
    setVoicePlaying(true);
    const result = await playStepVoice({
      text: speakableText,
      prayerId: activePrayer?.id,
      mystery: misterioActual,
      sequenceIndex: displayIndex,
      lang: voiceLangPref,
      preferBundled: true,
    });
    finishVoiceResult(result);
  }, [
    isTransitioning,
    speakableText,
    activePrayer?.id,
    misterioActual,
    displayIndex,
    voiceLangPref,
    finishVoiceResult,
  ]);
  playCurrentVoiceStepRef.current = playCurrentVoiceStep;

  // Step change while once/auto — not used for OFF→ONCE (that plays from tap / gesture).
  useEffect(() => {
    if (isTransitioning) return undefined;
    if (voiceModeRef.current === VOICE_MODES.OFF) return undefined;

    let cancelled = false;
    setVoicePlaying(true);
    (async () => {
      const result = await playStepVoice({
        text: speakableText,
        prayerId: activePrayer?.id,
        mystery: misterioActual,
        sequenceIndex: displayIndex,
        lang: voiceLangPref,
        preferBundled: true,
      });
      if (cancelled) return;
      finishVoiceResult(result);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceStepKey, isTransitioning]);

  const handleVoiceControlTap = useCallback(() => {
    const prev = voiceModeRef.current;
    const next = nextVoiceMode(prev, 'tap');
    if (next === VOICE_MODES.OFF) {
      stopStepVoice();
      setVoicePlaying(false);
      autoRosaryStartRef.current = null;
      voiceModeRef.current = next;
      setVoiceMode(next);
      return;
    }
    if (next === VOICE_MODES.AUTO && isValidRosaryMystery(misterioActual)) {
      if (!autoRosaryStartRef.current) autoRosaryStartRef.current = misterioActual;
    }
    voiceModeRef.current = next;
    setVoiceMode(next);
    // Start playback inside the user gesture (iOS/Chrome autoplay).
    if (prev === VOICE_MODES.OFF) {
      void playCurrentVoiceStep();
    }
  }, [misterioActual, playCurrentVoiceStep]);

  useEffect(() => {
    return () => stopStepVoice();
  }, []);

  useEffect(() => {
    const onExternalStop = () => {
      stopStepVoice();
      voiceModeRef.current = VOICE_MODES.OFF;
      setVoiceMode(VOICE_MODES.OFF);
      setVoicePlaying(false);
      autoRosaryStartRef.current = null;
    };
    window.addEventListener('rosario-voice-external-stop', onExternalStop);
    return () => window.removeEventListener('rosario-voice-external-stop', onExternalStop);
  }, []);

  // Cola → Liber: enter AUTO once after mystery is applied.
  useEffect(() => {
    const enterPlaylistAuto = (want) => {
      if (want && want !== misterioActualRef.current) return false;
      clearPendingAuto();
      // Avoid double-start when event + mount-claim both fire.
      if (voiceModeRef.current === VOICE_MODES.AUTO) return true;
      const m = misterioActualRef.current;
      if (isValidRosaryMystery(m)) {
        if (!autoRosaryStartRef.current) autoRosaryStartRef.current = m;
      } else {
        autoRosaryStartRef.current = null;
      }
      preserveAutoOnMysteryChangeRef.current = true;
      voiceModeRef.current = VOICE_MODES.AUTO;
      setVoiceMode(VOICE_MODES.AUTO);
      void playCurrentVoiceStepRef.current?.();
      return true;
    };

    const onSetAuto = (e) => {
      const want = e?.detail?.mysteryId;
      const attempt = Number(e?.detail?.attempt) || 0;
      if (want && want !== misterioActualRef.current) {
        if (attempt >= 20) return;
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('rosario-playlist-set-auto', {
              detail: { mysteryId: want, attempt: attempt + 1 },
            })
          );
        }, 50);
        return;
      }
      enterPlaylistAuto(want);
    };
    window.addEventListener('rosario-playlist-set-auto', onSetAuto);
    return () => window.removeEventListener('rosario-playlist-set-auto', onSetAuto);
  }, []);

  // Mount-safe claim: Cola navigates here before Liber has listeners.
  useEffect(() => {
    const want = peekPendingAutoMystery();
    if (!want || want !== misterioActual) return undefined;
    const t = window.setTimeout(() => {
      if (peekPendingAutoMystery() !== misterioActualRef.current) return;
      if (voiceModeRef.current === VOICE_MODES.AUTO) {
        clearPendingAuto();
        return;
      }
      clearPendingAuto();
      if (isValidRosaryMystery(misterioActualRef.current)) {
        if (!autoRosaryStartRef.current) autoRosaryStartRef.current = misterioActualRef.current;
      } else {
        autoRosaryStartRef.current = null;
      }
      preserveAutoOnMysteryChangeRef.current = true;
      voiceModeRef.current = VOICE_MODES.AUTO;
      setVoiceMode(VOICE_MODES.AUTO);
      void playCurrentVoiceStepRef.current?.();
    }, 100);
    return () => window.clearTimeout(t);
  }, [misterioActual]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        goPrev();
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goPrev, goNext]);

  // BottomNav ‹ › icons (same bar as Libro/Rosa) — keep booklet footer for Devociones only.
  useEffect(() => {
    const onStep = (event) => {
      const dir = event?.detail?.dir;
      if (dir < 0) goPrev();
      else if (dir > 0) goNext();
    };
    window.addEventListener('rosario-booklet-step', onStep);
    return () => window.removeEventListener('rosario-booklet-step', onStep);
  }, [goPrev, goNext]);

  const stepContext = useMemo(
    () => getBookletStepContext(secuencia, displayIndex, total),
    [secuencia, displayIndex, total]
  );

  const chromePhaseClass =
    transitionPhase === TRANSITION_PHASE.LEAVING
      ? ' booklet-prayer-chrome--leaving'
      : transitionPhase === TRANSITION_PHASE.LINGER ||
          transitionPhase === TRANSITION_PHASE.ARRIVING
        ? ' booklet-prayer-chrome--hidden'
        : transitionPhase === TRANSITION_PHASE.ENTERING
          ? ' booklet-prayer-chrome--entering'
          : '';

  const aveRunInfo = stepContext.aveRun;
  const mercyRunInfo = stepContext.mercyRun;
  const tripletRunInfo = stepContext.tripletRun;
  const isAveMaria = activePrayer?.id === 'A' && aveRunInfo && !isMercy;
  const isMercyPassion = activePrayer?.id === 'MP' && mercyRunInfo;
  const isMercyDecade = activePrayer?.id === 'EF' && stepContext.kind === 'decade';
  const isHolyGod = activePrayer?.id === 'HG' && tripletRunInfo;
  const isOptionalMercyStep =
    isMercy && (activePrayer?.id === 'DMO1' || activePrayer?.id === 'DMO2');
  const displayPrayerTitle = getBookletDisplayTitle(activePrayer, voiceLangPref, misterioActual);
  // Pacing runs (Ave ×10, Pasme, Santísimo, letanías) repeat one prayer many
  // times — the step itself has no real title, so the header shows only the
  // breadcrumb line instead of an invented repeated title.
  const showHeaderTitle = !(isAveMaria || isMercyPassion || isHolyGod || isLitany);

  // Breadcrumb suffixes after "N / total": only the runs that apply to this step.
  const novena = getNovenaConfig(misterioActual);
  const progressSuffixes = [
    novena && activePrayer?.id === novena.dayStepId
      && `Día ${novenaDay} de ${novena.total}`,
    isLitany && litanyVerseTotal > 0 && `letanía ${litanyVerseIndex + 1} / ${litanyVerseTotal}`,
    isPerVersePrayer && prayerVerseTotal > 0 && `verso ${prayerVerseIndex + 1} / ${prayerVerseTotal}`,
    isAveMaria && `${aveRunInfo.position} de ${aveRunInfo.total}`,
    isMercyPassion && `${mercyRunInfo.position} de ${mercyRunInfo.total}`,
    isHolyGod && `${tripletRunInfo.position} de ${tripletRunInfo.total}`,
    stepContext.kind === 'mystery' && stepContext.mysteryDecade && `misterio ${stepContext.mysteryDecade} de 5`,
    isMercyDecade && stepContext.mysteryDecade && `década ${stepContext.mysteryDecade} de 5`,
  ].filter(Boolean);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevIndexRef.current = safeIndex;
      return undefined;
    }
    prevIndexRef.current = safeIndex;
    return undefined;
  }, [safeIndex]);

  const vitralCandidates = useMemo(() => {
    if (!activePrayer) return [];
    const scrub = (list) => (list || []).filter((u) => u && !isTextHeavyImagePath(u));
    if (isLitany && litanyVerse) {
      const all = scrub(getLitanyVerseImageCandidates(litanyVerse, activePrayer, litanyVerseIndex));
      const picked = scrub([resolveLitanyVerseImage(litanyVerse, activePrayer, litanyVerseIndex)])[0]
        || all[0];
      return [picked, ...all.filter((u) => u !== picked)].filter(Boolean);
    }
    if (isPerVersePrayer) {
      const all = scrub(
        getPrayerVerseImageCandidates(
          activePrayer.id,
          prayerVerseIndex,
          activePrayer,
          misterioActual
        )
      );
      const picked = scrub([
        resolvePrayerVerseImage(
          activePrayer.id,
          prayerVerseIndex,
          activePrayer,
          misterioActual,
          occurrence + dayOffset
        ),
      ])[0] || all[0];
      return [picked, ...all.filter((u) => u !== picked)].filter(Boolean);
    }
    const all = scrub(getPrayerImageCandidates(activePrayer, misterioActual));
    // Occurrence, not raw position: `safeIndex` is the step being animated TOWARD
    // while `activePrayer` is secuencia[displayIndex], so seeding by safeIndex
    // meant the image belonged to a different step than the one on screen.
    const picked = pickPrayerImage(all, occurrence, countPrayerPinnedSources(activePrayer), dayOffset);
    return [picked, ...all.filter((u) => u !== picked)].filter(Boolean);
  }, [
    activePrayer,
    occurrence,
    dayOffset,
    isLitany,
    litanyVerse,
    litanyVerseIndex,
    isPerVersePrayer,
    prayerVerseIndex,
    misterioActual,
  ]);

  const devotionChrome = getDevotionChrome(misterioActual);

  const shareProgressLabel = useMemo(
    () =>
      formatBookletShareProgress({
        displayIndex,
        total,
        isSagradoCorazon,
        misterioActual,
        novenaDay,
        activePrayerId: activePrayer?.id,
        isLitany,
        litanyVerseIndex,
        litanyVerseTotal,
        isPerVersePrayer,
        prayerVerseIndex,
        prayerVerseTotal,
        stepContext,
        isMercy,
        isAveMaria,
        aveRunInfo,
        mercyRunInfo,
        tripletRunInfo,
        isMercyDecade,
        isMercyPassion,
        isHolyGod,
      }),
    [
      displayIndex,
      total,
      isSagradoCorazon,
      misterioActual,
      novenaDay,
      activePrayer?.id,
      isLitany,
      litanyVerseIndex,
      litanyVerseTotal,
      isPerVersePrayer,
      prayerVerseIndex,
      prayerVerseTotal,
      stepContext,
      isMercy,
      isAveMaria,
      aveRunInfo,
      mercyRunInfo,
      tripletRunInfo,
      isMercyDecade,
      isMercyPassion,
      isHolyGod,
    ]
  );

  const shareDeepLink = useMemo(
    () =>
      buildShareDeepLink({
        misterioActual,
        displayIndex,
        novenaDay,
      }),
    [misterioActual, displayIndex, novenaDay]
  );

  const shareCardPayload = useMemo(
    () =>
      buildShareCardPayload({
        misterioActual,
        prayerTitle: displayPrayerTitle,
        prayerText: buildSharePrayerText({ displayText, isLitany, litanyVerse }),
        backgroundUrl: vitralCandidates[0],
        progressLabel: shareProgressLabel,
        shareUrl: shareDeepLink,
      }),
    [
      misterioActual,
      displayPrayerTitle,
      displayText,
      isLitany,
      litanyVerse,
      vitralCandidates,
      shareProgressLabel,
      shareDeepLink,
    ]
  );

  const closeSharePreview = useCallback(() => {
    if (sharePreviewUrl) URL.revokeObjectURL(sharePreviewUrl);
    setSharePreviewUrl(null);
    setSharePreviewOpen(false);
    shareBlobRef.current = null;
  }, [sharePreviewUrl]);

  const handleSharePrayer = useCallback(async () => {
    if (isSharing || isTransitioning || !activePrayer) return;
    setIsSharing(true);
    try {
      // Mount the off-screen capture host, then wait for the vitral art
      // to actually decode inside the card before snapshotting.
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      await waitForShareReady(shareCardRef.current, shareCardPayload.backgroundUrl);
      const blob = await captureShareCardPng(shareCardRef.current);
      shareBlobRef.current = blob;
      const filename = makeShareFilename(displayPrayerTitle);
      const outcome = await deliverSharePng(blob, {
        filename,
        title: displayPrayerTitle,
        // Short caption — the prayer text itself travels ON the image.
        text: `${shareCardPayload.devotionTitle}${shareCardPayload.prayerTitle ? ` — ${shareCardPayload.prayerTitle}` : ''}${shareCardPayload.progressLabel ? ` (${shareCardPayload.progressLabel})` : ''}`,
        url: shareDeepLink,
      });
      // 'downloaded' used to vanish silently on desktop — show the card
      // so the user sees (and can re-share) the generated image.
      if (outcome === 'preview' || outcome === 'downloaded') {
        setSharePreviewUrl(URL.createObjectURL(blob));
        setSharePreviewOpen(true);
      }
    } catch (_) {
      if (shareBlobRef.current) {
        setSharePreviewUrl(URL.createObjectURL(shareBlobRef.current));
        setSharePreviewOpen(true);
      }
    } finally {
      setIsSharing(false);
    }
  }, [
    isSharing,
    isTransitioning,
    activePrayer,
    shareCardPayload,
    shareDeepLink,
    displayPrayerTitle,
  ]);

  const handleSharePreviewDownload = useCallback(() => {
    const blob = shareBlobRef.current;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = makeShareFilename(displayPrayerTitle);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [displayPrayerTitle]);

  const handleOutlineSelect = useCallback(
    (index) => {
      setOutlineOpen(false);
      navigateTo(index);
    },
    [navigateTo]
  );

  useEffect(() => () => {
    if (sharePreviewUrl) URL.revokeObjectURL(sharePreviewUrl);
  }, [sharePreviewUrl]);

  if (!activePrayer) {
    return (
      <div className="booklet-view booklet-view--empty">
        <p>No se encontraron oraciones para este misterio.</p>
      </div>
    );
  }

  const activeVariantLabel = variants?.find((v) => v.id === variantId)?.label;

  const vitralStyle = stepContextToVitralVars(stepContext);
  const vitralKind = isAveMaria || isMercyPassion
    ? 'ave'
    : stepContext.kind === 'mystery' || stepContext.kind === 'decade'
      ? 'mystery'
      : 'prayer';

  // Actions ride the mystery pills line when present; in devotion mode they
  // fall back to the meta row (no pills).
  const headerActions = (
    <div className="booklet-meta-row__actions" role="group" aria-label="Oraciones y compartir">
      {showRosaryPills && (
        <button
          type="button"
          className={`booklet-optional-btn${optionalGlow ? ' booklet-optional-btn--glow' : ''}`}
          onClick={() => openOptionalPrayer('guardian')}
          title="Oraciones opcionales: Ángel de la Guarda y San Benito"
          aria-label="Oraciones opcionales: Ángel de la Guarda y San Benito"
        >
          <span className="booklet-optional-btn__star" aria-hidden>✦</span>
          <span className="booklet-optional-btn__label">Ángel · Benito</span>
        </button>
      )}
      <button
        type="button"
        className="booklet-meta-row__share"
        onClick={handleSharePrayer}
        disabled={isTransitioning || isSharing}
        title="Compartir esta oración"
        aria-label="Compartir esta oración"
      >
        {isSharing ? '…' : '↗'}
      </button>
    </div>
  );

  return (
    <div className="booklet-view" style={vitralStyle} onPointerDown={resetOptionalIdle}>
      <VitralBackground
        candidates={vitralCandidates}
        kind={vitralKind}
        stepGlow={stepGlow}
        crossfade
        onReady={handleImageReady}
        useBookletClasses
      />

      <div className={`booklet-prayer-chrome${chromePhaseClass}`}>
        <header className="booklet-header">
          {showRosaryPills && !shelfOpen && (
          <div className="booklet-mystery-bar">
            <div className="booklet-mystery-row">
              {MYSTERY_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`booklet-mystery-pill${
                    misterioActual === opt.id ? ' booklet-mystery-pill--active' : ''
                  }`}
                  onClick={() => onMysteryChange?.(opt.id)}
                  disabled={isTransitioning}
                  aria-label={opt.label}
                >
                  <img
                    src={opt.img}
                    alt={opt.label}
                    className="booklet-mystery-pill__thumb-img"
                    draggable={false}
                  />
                  <span className="booklet-mystery-pill__thumb-label">{opt.label}</span>
                </button>
              ))}
              {headerActions}
            </div>
          </div>
          )}
          {novena && (
            <div className="booklet-novena-selector">
              <span className="booklet-novena-selector__label">Día</span>
              {getNovenaDays(misterioActual).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    resetOptionalIdle();
                    onNovenaDayChange?.(d);
                  }}
                  disabled={isTransitioning}
                  className={`booklet-novena-day-btn${novenaDay === d ? ' booklet-novena-day-btn--active' : ''}`}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
          {!shelfOpen && (
          <div className={`booklet-meta-row${isMercy ? ' booklet-meta-row--mercy' : ''}`}>
            {!showRosaryPills && headerActions}
            <div className="booklet-meta-row__reading" role="group" aria-label="Lectura y voz">
            <PrayerRecorder
              prayerId={activePrayer.id}
              prayerTitle={displayPrayerTitle}
              mystery={misterioActual}
              sequenceIndex={displayIndex}
              simpleMode={simpleMode}
              placement="title"
              isLeftHanded={isLeftHanded}
              voiceLang={voiceLangPref}
              voiceControlEnabled
              voiceMode={voiceMode}
              voicePlaying={voicePlaying}
              onVoiceControlTap={handleVoiceControlTap}
            >
            <div className="booklet-header-line">
            {showHeaderTitle && displayPrayerTitle ? (
              <h1
                className="booklet-title booklet-title--inline"
                style={{ fontSize: simpleMode ? '1.05rem' : '0.98rem' }}
              >
                {displayPrayerTitle}
                {isOptionalMercyStep && (
                  <span className="booklet-optional-chip">opcional</span>
                )}
              </h1>
            ) : null}
            {!showHeaderTitle && displayPrayerTitle ? (
              <span className="booklet-run-title">{displayPrayerTitle}</span>
            ) : null}
            <button
              type="button"
              className="booklet-meta-row__outline"
              onClick={() => setOutlineOpen(true)}
              disabled={isTransitioning || total < 2}
              title="Ver recorrido de la devoción"
              aria-label={`${devotionChrome.title}, paso ${displayIndex + 1} de ${total}. Ver recorrido`}
              data-testid="booklet-progress"
            >
              <span className="booklet-meta-row__step">
                {isOptionalMercyStep && (
                  <span className="booklet-mercy-info" title="Oración opcional de apertura" aria-hidden>
                    ⓘ{' '}
                  </span>
                )}
                <span className="booklet-meta-row__position">
                {isSagradoCorazon
                  ? `Paso ${displayIndex + 1} de ${total}`
                  : `${displayIndex + 1} / ${total}`}
                </span>
                {progressSuffixes.length > 0 && (
                  <span className="booklet-ave-count">
                    {' '}
                    · {progressSuffixes.join(' · ')}
                  </span>
                )}
              </span>
            </button>
            {variants && (
              <button
                type="button"
                className="booklet-variant-chip"
                onClick={cycleVariant}
                aria-label={`Cambiar versión del ${activePrayer.title}`}
                disabled={isTransitioning}
                title="Tocar para otra versión"
              >
                ◇ {activeVariantLabel}
              </button>
            )}
            </div>
            </PrayerRecorder>
            </div>

          </div>
          )}
        </header>

        <BookletPrayerPanel
          displayText={displayText}
          simpleMode={simpleMode}
          isAveMaria={Boolean(isAveMaria)}
          isLitany={isLitany}
          litanyVerse={litanyVerse}
          litanyVerseIndex={litanyVerseIndex}
          litanyVerseTotal={litanyVerseTotal}
          litanySections={activePrayer.sections}
          misterioActual={misterioActual}
          stepContext={stepContext}
          variant="booklet"
          isTransitioning={isTransitioning}
          onTapNav={(dir) => (dir === 'next' ? goNext() : goPrev())}
        />
      </div>

      <OfferingLight
        active={offeringLight}
        count={Math.max(loadPrayForIntentions().length, 1)}
      />

      {optionalOpen && (
        <AppOverlay>
        <OptionalPrayerSheet
          key={optionalPrayerId}
          initialPrayerId={optionalPrayerId}
          onClose={() => setOptionalOpen(false)}
        />
        </AppOverlay>
      )}

      {showLitanyEntrance && litanyEntranceEnabled && (
        <LitanyEntrance
          currentMystery={misterioActual}
          onComplete={() => setShowLitanyEntrance(false)}
        />
      )}

      {isSharing &&
        createPortal(
          <div className="booklet-share-capture-host" aria-hidden="true">
            <PrayerShareCard ref={shareCardRef} {...shareCardPayload} />
          </div>,
          document.body
        )}

      {outlineOpen && (
        <AppOverlay>
        <BookletOutlineView
          steps={secuencia}
          currentIndex={displayIndex}
          devotionTitle={devotionChrome.title}
          devotionSubtitle={devotionChrome.subtitle}
          mysteryType={misterioActual}
          onSelectStep={handleOutlineSelect}
          onClose={() => setOutlineOpen(false)}
        />
        </AppOverlay>
      )}

      {sharePreviewOpen && (
        <AppOverlay>
        <PrayerSharePreviewModal
          imageUrl={sharePreviewUrl}
          onClose={closeSharePreview}
          onDownload={handleSharePreviewDownload}
        />
        </AppOverlay>
      )}

      {/* Host only — toggle lives in BottomNav (Libro). */}
      <DevotionsShelf
        externalToggle
        misterioActual={misterioActual}
        active={!showRosaryPills}
        open={shelfOpen}
        onOpenChange={setShelfOpen}
        recorridos={
          SHELF_RECORRIDOS.map((item) =>
            renderShelfRecorrido(item, { misterioActual, onShelfDevotionClick, onMysteryChange })
          )
        }
        breves={
          SHELF_BREVES.map((item) => (
            <ShelfItem key={item.prayerId} label={item.label}>
              <MercyWindowThumb
                active={optionalOpen && optionalPrayerId === item.prayerId}
                onClick={() => openOptionalPrayer(item.prayerId)}
                title={item.title}
                img={optionalPrayerThumbnail(item.prayerId)}
                badge={item.badge}
              />
            </ShelfItem>
          ))
        }
        proximas={upcomingDevotionThumbs().map((dev) => (
          <ShelfItem key={dev.id} label={dev.label} soon>
            <MercyWindowThumb
              soon
              title={`${dev.label} — ${dev.note}`}
              img={dev.img}
            />
          </ShelfItem>
        ))}
        onReturnToRosary={
          !isValidRosaryMystery(misterioActual)
            ? () => onMysteryChange?.(getDefaultMystery())
            : undefined
        }
      />
    </div>
  );
}
